import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Trash2, Edit2, Check, X, Tag } from "lucide-react";
import type { TaskCategory } from "../types";
import { useTaskCategories, useTaskCategoriesActions } from "./hooks";
import { CATEGORY_COLORS } from "./taskCategoriesStore";
import { globalTasksStore } from "../stores/globalTasksStore";
import { PageContainer, PageHeader, PageEmptyState } from "../components/PageLayout";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

export function CategoriesPage() {
  useDocumentTitle('Categories – qtplan');
  const { t } = useTranslation();
  const categories = useTaskCategories();
  const { addNewCategory, updateCategory, deleteCategory } = useTaskCategoriesActions();

  const [showAddRow, setShowAddRow] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(CATEGORY_COLORS[0]);
  const addInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showAddRow) {
      addInputRef.current?.focus();
    }
  }, [showAddRow]);

  const handleAdd = () => {
    const name = newName.trim();
    if (!name) return;
    addNewCategory(name, newColor);
    setNewName("");
    // Cycle to next color
    const idx = CATEGORY_COLORS.indexOf(newColor);
    setNewColor(CATEGORY_COLORS[(idx + 1) % CATEGORY_COLORS.length]);
    // Keep the add row open for quick successive adds
    addInputRef.current?.focus();
  };

  const handleCloseAddRow = () => {
    setShowAddRow(false);
    setNewName("");
  };

  const handleDelete = (categoryId: string) => {
    // Count tasks using this category
    const { tasksById, taskIds } = globalTasksStore.getSnapshot().context;
    const taskCount = taskIds.filter((id) => {
      const t = tasksById[id];
      return t?.categoryIds?.includes(categoryId);
    }).length;

    const msg = taskCount > 0
      ? t('categories.deleteConfirmWithTasks', { count: taskCount })
      : t('categories.deleteConfirm');

    if (!confirm(msg)) return;

    // Remove categoryId from all tasks
    if (taskCount > 0) {
      for (const id of taskIds) {
        const t = tasksById[id];
        if (t?.categoryIds?.includes(categoryId)) {
          globalTasksStore.send({
            type: "task.updateCategories",
            taskId: id,
            categoryIds: t.categoryIds.filter((c) => c !== categoryId),
          });
        }
      }
    }

    deleteCategory(categoryId);
  };

  return (
    <PageContainer wide>
      <PageHeader
        title={t('categories.title')}
        subtitle={t('categories.count', { count: categories.length })}
        buttonLabel={t('categories.addButton')}
        onButtonClick={() => setShowAddRow(true)}
      />

      {categories.length === 0 && !showAddRow ? (
        <PageEmptyState
          icon={<Tag size={20} />}
          title={t('categories.noCategories')}
          description={t('categories.noCategoriesDesc')}
        />
      ) : (
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="divide-y divide-gray-100">
            {showAddRow && (
              <div className="px-3 py-1.5 flex items-center gap-2">
                <div className="flex gap-1">
                  {CATEGORY_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewColor(c)}
                      className={`w-4 h-4 rounded-full transition-all ${
                        newColor === c
                          ? "ring-1 ring-offset-0 ring-gray-500 scale-110"
                          : "hover:scale-105"
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <input
                  ref={addInputRef}
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAdd();
                    if (e.key === "Escape") handleCloseAddRow();
                  }}
                  placeholder={t('categories.newPlaceholder')}
                  className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
                />
                <button
                  onClick={handleCloseAddRow}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            )}
            {categories.map((cat) => (
              <CategoryRow
                key={cat.id}
                category={cat}
                onUpdate={(updates) => updateCategory(cat.id, updates)}
                onDelete={() => handleDelete(cat.id)}
              />
            ))}
          </div>
        </div>
      )}
    </PageContainer>
  );
}

function CategoryRow({
  category,
  onUpdate,
  onDelete,
}: {
  category: TaskCategory;
  onUpdate: (updates: Partial<Omit<TaskCategory, "id">>) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(category.name);
  const [editColor, setEditColor] = useState(category.color);

  const handleSave = () => {
    const name = editName.trim();
    if (!name) return;
    onUpdate({ name, color: editColor });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditName(category.name);
    setEditColor(category.color);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="px-3 py-1.5 space-y-2">
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") handleCancel();
          }}
          className="w-full px-2 py-1 border border-gray-200 rounded text-xs focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
          autoFocus
        />
        <div className="flex flex-wrap gap-1">
          {CATEGORY_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setEditColor(c)}
              className={`w-4 h-4 rounded-full transition-all ${
                editColor === c
                  ? "ring-1 ring-offset-0 ring-gray-500 scale-110"
                  : "hover:scale-105"
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            className="flex items-center gap-1 px-2 py-1 text-xs text-green-700 hover:bg-green-50 rounded"
          >
            <Check size={14} />
            {t('common.save')}
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
          >
            <X size={14} />
            {t('common.cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 transition-colors">
      <span
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: category.color }}
      />
      <span className="flex-1 text-xs font-medium text-gray-900">
        {category.name}
      </span>
      <button
        onClick={() => setIsEditing(true)}
        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
        title={t('common.edit')}
      >
        <Edit2 size={14} />
      </button>
      <button
        onClick={onDelete}
        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
        title={t('common.delete')}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}
